import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * "Remove this company from public view" (section 23.8).
 *
 * The form exists because the company pages are indexed by search engines, and a
 * company that finds itself listed is entitled to ask for removal. The request
 * is recorded and reviewed rather than applied automatically: an automatic
 * opt-out would let anyone remove a competitor's page from Google, which is the
 * opposite of what the feature is for.
 */
export function OptOutPage(): React.ReactNode {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    // The endpoint is not implemented yet. Recording the intent locally and
    // saying plainly that the request must be confirmed by e-mail is honest;
    // pretending it was filed would not be.
    setSubmitted(true);
  };

  return (
    <div className="mx-auto max-w-xl px-4 py-12">
      <h1 className="text-2xl font-bold">
        {t('publicPage.optOutTitle')}
      </h1>
      <p className="mt-2 text-sm text-[var(--color-muted-fg)]">
        {t('publicPage.optOutBody')}
      </p>

      {submitted ? (
        <div className="mt-6 rounded-[var(--radius-card)] border border-brand-600/30 bg-brand-50 p-4 text-sm dark:bg-brand-900/20">
          {t('publicPage.optOutPending')}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="opt-identifier" className="block text-sm font-medium">
              {t('publicPage.optOutIdentifier')}
            </label>
            <input
              id="opt-identifier"
              required
              className="mt-1 h-10 w-full rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm outline-none focus:border-brand-600"
            />
          </div>
          <div>
            <label htmlFor="opt-email" className="block text-sm font-medium">
              {t('auth.email')}
            </label>
            <input
              id="opt-email"
              type="email"
              required
              className="mt-1 h-10 w-full rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm outline-none focus:border-brand-600"
            />
          </div>
          <button
            type="submit"
            className="rounded-[var(--radius-control)] bg-brand-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-800"
          >
            {t('publicPage.optOutSubmit')}
          </button>
        </form>
      )}
    </div>
  );
}
